import {createStyles, Popover, ScrollArea, Text, Tooltip} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useEffect, useRef, useState} from 'react';

import {SITE_MARKER_COLOR} from 'antibodies/AntibodyConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

// A marker is concentric circles: a white backing, a colored ring, a white gap, and a colored core. These are
// the radii of the ring, the gap, and the core as fractions of the marker's radius, which is that of the
// backing. Formats are drawn in viewboxes of very different sizes, so they scale with the marker.
const RING_RATIO = 0.8;
const GAP_RATIO = 0.47;
const CORE_RATIO = 0.31;

// How much a marker grows while it is hovered.
const HOVER_SCALE = 1.3;

// The list of sites in the card of a marker that stands for several sites is this tall before it scrolls:
// five lines of small text (a line is 1.55 times the font size, which is 0.875rem).
const SITE_LIST_MAX_HEIGHT = '6.8rem';

// How long the card of a marker stays open after the pointer leaves the marker, so that the pointer can get
// from the marker to the card to scroll it.
const CARD_CLOSE_DELAY_MS = 150;

export interface AntibodySiteMarkerProps {
  // The center of the marker, in viewbox units.
  x: number;
  y: number;
  // Radius of the marker, including its white backing, in viewbox units.
  radius: number;
  // The label of each site the marker stands for, shown when hovering or focusing the marker.
  labels: ReadonlyArray<string>;
  // The number of sites the marker stands for. A marker for one site is drawn as a ring with a core, and
  // shows its label in a tooltip. A marker for any other number is drawn as a solid disc with the number,
  // and shows a scrollable list of the labels.
  count: number;
}

const useStyles = createStyles((theme) => ({
  // Clicking a marker focuses it, which shouldn't draw the browser's focus ring: that is for keyboard focus.
  focusable: {
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
  },
  // Grow from the center of the marker, which is where its circles are centered.
  marker: {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transition: 'transform 0.13s',
    cursor: 'pointer',
    '&:hover': {
      transform: `scale(${HOVER_SCALE})`,
    },
  },
  colored: {
    fill: SITE_MARKER_COLOR,
  },
  white: {
    fill: theme.white,
  },
  count: {
    fill: theme.white,
    fontWeight: 'bold',
    // The count belongs to the marker, so it shouldn't be selected or take hover from it.
    pointerEvents: 'none',
    userSelect: 'none',
  },
}));

// The size of the count in a marker for several sites, as a fraction of the marker's radius, which gets
// smaller for more digits so that the count fits in the disc.
function countFontSizeRatio(count: number): number {
  return count < 10 ? 1 : count < 100 ? 0.8 : 0.6;
}

function SiteList({labels}: {labels: ReadonlyArray<string>}): JSX.Element {
  // Only a vertical scrollbar is ever needed, so only the right of the list is offset for it: Mantine would
  // otherwise also pad the bottom by the size of a scrollbar, even when nothing scrolls.
  return (
    // @ts-expect-error This is a DefinitelyTyped bug https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/69006
    <ScrollArea.Autosize
      mah={SITE_LIST_MAX_HEIGHT}
      type="auto"
      offsetScrollbars={true}
      styles={{viewport: {paddingBottom: 0}}}
    >
      {labels.map((label, index) => (
        <Text key={index} size="sm">
          {label}
        </Text>
      ))}
    </ScrollArea.Autosize>
  );
}

/**
 * Shows a card listing `labels` next to `children` while the pointer is over either of them, or `children`
 * has keyboard focus. The card stays open for a moment after the pointer leaves, so that it can be reached
 * to scroll it.
 */
function SiteListCard({
  labels,
  children,
}: {
  labels: ReadonlyArray<string>;
  children: React.ReactNode;
}): JSX.Element {
  const [opened, setOpened] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelClose = (): void => {
    clearTimeout(closeTimer.current);
  };
  const open = (): void => {
    cancelClose();
    setOpened(true);
  };
  const closeSoon = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpened(false);
    }, CARD_CLOSE_DELAY_MS);
  };
  useEffect(() => cancelClose, []);

  return (
    <Popover
      opened={opened}
      onClose={() => {
        setOpened(false);
      }}
      withinPortal={true}
      shadow="md"
    >
      <Popover.Target>
        <g onMouseEnter={open} onMouseLeave={closeSoon} onFocus={open} onBlur={closeSoon}>
          {children}
        </g>
      </Popover.Target>
      <Popover.Dropdown onMouseEnter={open} onMouseLeave={closeSoon}>
        <SiteList labels={labels} />
      </Popover.Dropdown>
    </Popover>
  );
}

export interface AntibodySiteMarkerIconProps {
  // Radius of the marker, including its white backing, in viewbox units.
  radius: number;
  // The number of sites the marker stands for. See AntibodySiteMarkerProps.
  count: number;
  // Whether the marker grows while hovered, which shows that it can be hovered for its labels.
  growOnHover?: boolean;
}

/**
 * The circles of a site marker, centered on the origin. This is an SVG component, so it must be rendered
 * within an `<svg>`.
 */
export function AntibodySiteMarkerIcon({
  radius,
  count,
  growOnHover = false,
}: AntibodySiteMarkerIconProps): JSX.Element {
  const {classes} = useStyles();
  const isSingle = count === 1;

  return (
    <g className={growOnHover ? classes.marker : undefined}>
      <circle
        r={radius}
        className={classes.white}
        {...getTestProps({component: 'AntibodySiteMarker', element: 'backing'})}
      />
      <circle r={radius * RING_RATIO} className={classes.colored} />
      {isSingle ? (
        <>
          <circle r={radius * GAP_RATIO} className={classes.white} />
          <circle r={radius * CORE_RATIO} className={classes.colored} />
        </>
      ) : (
        <text
          className={classes.count}
          fontSize={radius * countFontSizeRatio(count)}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {count}
        </text>
      )}
    </g>
  );
}

/**
 * A marker drawn on top of an antibody (e.g. for a conjugation site). This is an SVG component, so it must
 * be rendered within an `<svg>`, and all units are 'abstract user units', relative to the SVG viewbox.
 *
 * The marker can be focused, which shows its labels as hovering it does. The label of a marker for one site
 * is shown in a tooltip, rather than an SVG `<title>`, because browsers show a `<title>` only after a long
 * delay. A marker for any other number of sites shows its labels in a card instead, which stays open while
 * the pointer is over it, since a tooltip would close before the list could be scrolled.
 */
export default function AntibodySiteMarker({
  x,
  y,
  radius,
  labels,
  count,
}: AntibodySiteMarkerProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const descriptionId = useId();
  const isSingle = count === 1;
  // The marker is positioned with a transform attribute on this group, and grows on hover with a CSS
  // transform on the group inside it: a CSS transform would replace the attribute.
  const marker = (
    <g
      className={classes.focusable}
      transform={`translate(${x}, ${y})`}
      role="img"
      aria-label={isSingle ? labels[0] : `${count} sites`}
      aria-describedby={isSingle ? undefined : descriptionId}
      tabIndex={0}
    >
      {isSingle ? null : <desc id={descriptionId}>{labels.join('; ')}</desc>}
      <AntibodySiteMarkerIcon radius={radius} count={count} growOnHover={true} />
    </g>
  );

  if (isSingle) {
    return (
      <Tooltip
        label={labels[0]}
        openDelay={0}
        withinPortal={true}
        events={{hover: true, focus: true, touch: false}}
      >
        {marker}
      </Tooltip>
    );
  }
  return <SiteListCard labels={labels}>{marker}</SiteListCard>;
}
